import React, { useState, useEffect } from "react";
import { useSupabaseAuth } from "@/context/SupabaseAuthContext";
import { User, LogOut, Map, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

interface UserAvatarProps {
  size?: "sm" | "md" | "lg";
  showDropdown?: boolean;
}

const UserAvatar: React.FC<UserAvatarProps> = ({
  size = "md",
  showDropdown = true,
}) => {
  const { user, signOut } = useSupabaseAuth();
  const navigate = useNavigate();
  const [avatarError, setAvatarError] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [fullName, setFullName] = useState<string | null>(null); // New state for full name

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;

      try {
        console.log("Fetching profile for user:", user.id);
        // Modified: Select both avatar_url and full_name
        const { data: profile, error } = await supabase
          .from("profiles")
          .select("avatar_url, full_name") // Include full_name here
          .eq("id", user.id)
          .single();

        if (error) {
          console.error("Error fetching profile:", error);
          // Set full name to null on error
          setFullName(null);
          return;
        }

        console.log("Profile data:", profile);

        // Set the full name from the fetched profile
        setFullName(profile?.full_name || null);

        if (profile?.avatar_url) {
          try {
            // Clean up the avatar_url path in case it contains the full URL
            const cleanPath = profile.avatar_url.replace(/^.*?avatars\//, "");
            console.log("Clean avatar path:", cleanPath);

            // Get a signed URL that's valid for 1 hour for the avatar
            const { data, error: signedUrlError } = await supabase.storage
              .from("avatars")
              .createSignedUrl(cleanPath, 3600);

            if (signedUrlError || !data) {
              console.error(
                "Failed to create signed URL for avatar:",
                signedUrlError,
              );
              setAvatarUrl(null);
              setIsImageLoaded(false);
              setAvatarError(true);
              return;
            }

            console.log("Generated signed URL:", data.signedUrl);

            // Preload the image to verify it exists and is accessible
            const img = new Image();
            img.crossOrigin = "anonymous";

            img.onload = () => {
              console.log("Avatar image loaded successfully:", data.signedUrl);
              setAvatarUrl(data.signedUrl);
              setIsImageLoaded(true);
              setAvatarError(false);
            };

            img.onerror = (e) => {
              console.error("Failed to load avatar image:", {
                error: e,
                url: data.signedUrl,
                storagePath: cleanPath,
              });
              setAvatarUrl(null);
              setIsImageLoaded(false);
              setAvatarError(true);
            };

            img.src = data.signedUrl;
          } catch (error) {
            console.error("Error creating signed URL:", error);
            setAvatarUrl(null);
            setIsImageLoaded(false);
            setAvatarError(true);
          }
        } else {
          console.log("No avatar_url found in profile");
          setAvatarUrl(null);
          setIsImageLoaded(false);
        }
      } catch (error) {
        console.error("Error in fetchProfile:", error);
        setAvatarUrl(null);
        setIsImageLoaded(false);
        setAvatarError(true);
      }
    };

    fetchProfile();
  }, [user]);

  if (!user) return null;

  // Format user's name from email (e.g., "jane.doe@example.com" -> "Jane Doe")
  const formatUserName = (email: string) => {
    const username = email.split("@")[0];
    return username
      .split(/[._-]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toUpperCase())
      .join(" ");
  };

  // Prioritize fetched fullName, then user_metadata, then formatted email
  const displayUserName =
    fullName ||
    user.user_metadata?.full_name ||
    formatUserName(user.email || "");

  const sizeClasses = {
    sm: "h-6 w-6",
    md: "h-8 w-8",
    lg: "h-10 w-10",
  };

  const iconSizes = {
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-5 w-5",
  };

  const avatar = (
    <Avatar
      className={cn("border border-border overflow-hidden", sizeClasses[size])}
    >
      {avatarUrl && !avatarError && isImageLoaded ? (
        <AvatarImage
          src={avatarUrl}
          alt={displayUserName} // Use displayUserName here
          className="object-cover w-full h-full"
          crossOrigin="anonymous"
          onError={(e) => {
            console.error("Error loading avatar image in AvatarImage:", {
              error: e,
              url: avatarUrl,
            });
            setAvatarError(true);
            setIsImageLoaded(false);
          }}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-primary/10">
          <User className={cn("text-primary", iconSizes[size])} />
        </div>
      )}
    </Avatar>
  );

  if (!showDropdown) {
    return (
      <div className="flex items-center">
        {avatar && (
          <div className="flex-shrink-0 bg-gray-500/10 rounded-full p-1">
            {avatar}
          </div>
        )}
        {displayUserName && (
          <span className="text-sm font-semibold bg-gray-500/10 px-3 py-2 -ml-2 pl-4 rounded-r-full">
            {displayUserName}
          </span>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Display the fetched full name */}
      <p>Hello!</p>
    </div>
  );
};

export default UserAvatar;
